import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import {
  Clock, ChevronLeft, ChevronRight, Flag, CheckCircle2, Circle,
  Loader2, Swords, Trophy, X, Crown,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ActiveH2H = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [match, setMatch] = useState(location.state?.match || null);
  const [loading, setLoading] = useState(!location.state?.match);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const startTimeRef = useRef(Date.now());
  const pollRef = useRef(null);

  // Bootstrap if landed without state
  useEffect(() => {
    if (match) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/h2h/match/${matchId}`);
        setMatch(data);
      } catch (e) {
        toast.error('Could not load match');
        navigate('/h2h');
      } finally {
        setLoading(false);
      }
    })();
  }, [match, matchId, navigate]);

  useEffect(() => {
    if (!match) return;
    startTimeRef.current = Date.now();
    setSecondsLeft(match.time_limit_seconds || 300);
  }, [match]);

  const handleSubmit = useCallback(async (auto = false) => {
    if (submitting || !match || result) return;
    setSubmitting(true);
    const payload = {
      answers: Object.entries(answers).map(([q_idx, selected]) => ({ q_idx: parseInt(q_idx, 10), selected })),
      time_taken_seconds: Math.floor((Date.now() - startTimeRef.current) / 1000),
    };
    try {
      const { data } = await axios.post(`${API_URL}/api/h2h/match/${match.id}/submit`, payload);
      setResult(data);
      if (auto) toast.warning('Time up! Submitted automatically.');
      else toast.success('Submitted! Waiting for opponent…');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
      setShowConfirm(false);
    }
  }, [submitting, match, answers, result]);

  // Timer tick
  useEffect(() => {
    if (!match || result) return;
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(t);
          handleSubmit(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [match, result, handleSubmit]);

  // After submit: poll for opponent's result + settlement
  useEffect(() => {
    if (!result || result.status === 'completed') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/h2h/match/${match.id}`);
        if (data.status === 'completed') {
          setResult({ ...data, answer_key: result.answer_key });
        }
      } catch (e) { /* silent */ }
    }, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [result, match]);

  if (loading || !match) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-[#0EA5E9]" />
        </div>
      </div>
    );
  }

  // ===== Result view =====
  if (result) {
    const youKey = result.you_are;
    const oppKey = youKey === 'player1' ? 'player2' : 'player1';
    const me = result[youKey];
    const opp = result[oppKey];
    const finished = result.status === 'completed';
    const youWon = finished && result.winner === youKey;
    const tied = finished && result.winner === 'draw';

    return (
      <div className="min-h-screen" data-testid="h2h-result-page">
        <Header />
        <main className="max-w-3xl mx-auto px-6 py-10">
          {!finished && (
            <div className="quiz-card p-6 mb-6 flex items-center gap-3 bg-amber-50 border-amber-200">
              <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
              <div>
                <p className="text-sm font-bold text-amber-900">Waiting for opponent to finish…</p>
                <p className="text-xs text-amber-700">Your score is locked in. Result will update automatically.</p>
              </div>
            </div>
          )}

          <div className="text-center mb-8">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-3xl shadow-lg mb-4 ${
              !finished ? 'bg-slate-300' : tied ? 'bg-gradient-to-br from-slate-400 to-slate-500'
              : youWon ? 'bg-gradient-to-br from-emerald-400 to-emerald-600'
              : 'bg-gradient-to-br from-rose-400 to-rose-600'
            }`}>
              {finished ? (youWon ? <Crown className="w-8 h-8 text-white" /> : tied ? <Swords className="w-8 h-8 text-white" /> : <X className="w-8 h-8 text-white" />)
                       : <Swords className="w-8 h-8 text-white" />}
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {!finished ? 'Submitted' : tied ? 'Draw' : youWon ? 'Victory!' : 'Defeat'}
            </h1>
            {finished && result.mode === 'ranked' && youWon && (
              <p className="text-base text-emerald-600 font-bold mt-1">+{result.prize_winner} pts credited</p>
            )}
          </div>

          {/* Score panel */}
          <div className="quiz-card p-6 mb-6" data-testid="h2h-scoreboard">
            <div className="grid grid-cols-2 gap-3">
              <PlayerPanel name={me?.name || 'You'} score={me?.score} totalQ={result.question_count} time={me?.time_taken_seconds} isWinner={youWon} highlight />
              <PlayerPanel name={opp?.name || 'Opponent'} score={opp?.score} totalQ={result.question_count} time={opp?.time_taken_seconds} isWinner={finished && result.winner === oppKey} />
            </div>
          </div>

          {/* Answer review */}
          {result.answer_key && (
            <div className="quiz-card p-6 mb-6">
              <h2 className="text-lg font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>Answer Review</h2>
              <div className="space-y-2.5 text-sm">
                {result.answer_key.map((q) => {
                  const sel = answers[q.q_idx] || '—';
                  const isCorrect = sel === q.answer;
                  return (
                    <div key={q.q_idx} className={`p-3 rounded-xl border ${isCorrect ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                      <div className="flex justify-between text-xs font-bold mb-0.5">
                        <span>Q{q.q_idx + 1}</span>
                        <span className={isCorrect ? 'text-emerald-700' : 'text-rose-700'}>
                          You: {sel} · Correct: {q.answer}
                        </span>
                      </div>
                      {q.explanation && <p className="text-xs text-[#6B7280]">{q.explanation}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => navigate('/h2h')}
              data-testid="back-to-h2h-btn"
              className="flex-1 px-5 py-3 rounded-2xl font-bold text-sm bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-[0_4px_0_rgb(180,83,9)] active:translate-y-1 active:shadow-none"
            >
              Find another match
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ===== Active match view =====
  const q = match.questions[currentIdx];
  const totalQ = match.questions.length;
  const answeredCount = Object.keys(answers).length;
  const mm = Math.floor(secondsLeft / 60).toString().padStart(2, '0');
  const ss = (secondsLeft % 60).toString().padStart(2, '0');
  const timeWarning = secondsLeft < 60;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="active-h2h-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Top strip with opponent */}
        <div className="quiz-card p-4 mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shadow-md">
              <Swords className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#6B7280]">
                {match.you_are === 'player1' ? match.player1?.name : match.player2?.name} vs {match.you_are === 'player1' ? match.player2?.name : match.player1?.name}
              </p>
              <p className="text-sm font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {match.topic_label} · {answeredCount}/{totalQ}
              </p>
            </div>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-2xl font-black text-lg ${timeWarning ? 'bg-rose-100 text-rose-700 animate-pulse' : 'bg-indigo-100 text-indigo-700'}`} data-testid="h2h-timer">
            <Clock className="w-5 h-5" />
            {mm}:{ss}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-5">
          {/* Question */}
          <div className="quiz-card p-6" data-testid="question-card">
            <div className="flex items-center justify-between mb-4">
              <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-slate-100 text-slate-700">{q.subject} · {q.topic}</span>
              <span className="text-xs text-[#6B7280]">Q {currentIdx + 1} / {totalQ}</span>
            </div>
            <h2 className="text-base sm:text-lg text-[#111827] mb-6 whitespace-pre-line leading-relaxed">{q.question}</h2>
            <div className="space-y-2.5">
              {q.options.map((opt) => {
                const letter = (opt.match(/^([A-D])[.)]/) || [])[1] || opt.charAt(0).toUpperCase();
                const isSelected = answers[currentIdx] === letter;
                return (
                  <button
                    key={opt}
                    onClick={() => setAnswers({ ...answers, [currentIdx]: letter })}
                    data-testid={`option-${letter}`}
                    className={`w-full text-left px-4 py-3 rounded-2xl border-2 transition-all flex items-start gap-3 ${
                      isSelected ? 'border-[#0EA5E9] bg-cyan-50' : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    {isSelected ? <CheckCircle2 className="w-5 h-5 text-[#0EA5E9] shrink-0 mt-0.5" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />}
                    <span className="text-sm text-[#111827]">{opt}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-between items-center mt-6 pt-5 border-t border-slate-100">
              <button onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))} disabled={currentIdx === 0} data-testid="prev-q-btn"
                className="px-4 py-2.5 rounded-xl font-bold text-sm bg-slate-100 text-[#6B7280] hover:bg-slate-200 disabled:opacity-40 flex items-center gap-1">
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              {currentIdx === totalQ - 1 ? (
                <button onClick={() => setShowConfirm(true)} data-testid="submit-h2h-btn" className="px-5 py-2.5 rounded-xl font-bold text-sm bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5 shadow-md">
                  <Flag className="w-4 h-4" /> Submit
                </button>
              ) : (
                <button onClick={() => setCurrentIdx(Math.min(totalQ - 1, currentIdx + 1))} data-testid="next-q-btn"
                  className="px-4 py-2.5 rounded-xl font-bold text-sm bg-[#0EA5E9] text-white hover:bg-[#0284C7] flex items-center gap-1">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Palette */}
          <aside className="quiz-card p-5 h-fit" data-testid="palette">
            <h3 className="text-sm font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>Questions</h3>
            <div className="grid grid-cols-5 gap-1.5">
              {match.questions.map((_, idx) => (
                <button key={idx} onClick={() => setCurrentIdx(idx)} data-testid={`palette-${idx}`}
                  className={`aspect-square rounded-md text-xs font-black ${
                    idx === currentIdx ? 'ring-2 ring-[#0EA5E9] bg-cyan-50 text-[#0EA5E9]' :
                    answers[idx] ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}>{idx + 1}</button>
              ))}
            </div>
            <button onClick={() => setShowConfirm(true)} className="w-full mt-4 px-3 py-2.5 rounded-xl font-bold text-sm bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center gap-1.5 shadow-md">
              <Flag className="w-4 h-4" /> Submit
            </button>
          </aside>
        </div>
      </main>

      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="confirm-modal">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-black text-[#111827] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>Submit to opponent?</h3>
            <p className="text-sm text-[#6B7280] mb-5">{answeredCount}/{totalQ} answered. Once submitted you can't change answers.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirm(false)} className="flex-1 px-4 py-2.5 rounded-xl font-bold text-sm bg-slate-100 text-[#6B7280] hover:bg-slate-200">Keep solving</button>
              <button onClick={() => handleSubmit(false)} disabled={submitting} data-testid="confirm-submit-btn"
                className="flex-1 px-4 py-2.5 rounded-xl font-bold text-sm bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center gap-1.5">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting</> : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PlayerPanel = ({ name, score, totalQ, time, isWinner, highlight }) => (
  <div className={`p-4 rounded-2xl ${highlight ? 'bg-cyan-50 border-2 border-cyan-200' : 'bg-slate-50 border-2 border-slate-200'}`}>
    <div className="flex items-center justify-between mb-1.5">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 truncate">{name}</p>
      {isWinner && <Trophy className="w-4 h-4 text-amber-500" />}
    </div>
    <p className="text-3xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
      {score ?? '—'}<span className="text-base text-[#9CA3AF]"> / {totalQ}</span>
    </p>
    <p className="text-xs text-[#6B7280] mt-1">{time != null ? `${time}s` : 'Still solving…'}</p>
  </div>
);

export default ActiveH2H;
